import { BYTES_PER_MB, MIME_TYPES } from "@/src/constants/files-extensions";

/**
 * Supported extensions
 */
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "avi", "mkv", "webm"];

/**
 * Extract file extension from URL
 */
export const getFileExtension = (url: string): string => {
  if (!url) return "file";
  const filename = url.split("/").pop()?.split("?")[0] ?? "";
  return filename.split(".").pop()?.toLowerCase() ?? "file";
};

/**
 * Get filename from URL
 */
export const getFilename = (url: string | null | undefined): string => {
  if (!url) return "file";
  return url.split("/").pop()?.split("?")[0] ?? "file";
};
/**
 * MIME type resolver
 */
export const getMimeType = (ext: string) =>
  MIME_TYPES[ext] ?? "application/octet-stream";

/**
 * Type checks
 */
export const isImageFile = (url: string | null | undefined): boolean =>
  !!url && IMAGE_EXTENSIONS.includes(getFileExtension(url));

export const isVideoFile = (url: string | null | undefined): boolean =>
  !!url && VIDEO_EXTENSIONS.includes(getFileExtension(url));

export const isMediaFile = (url: string | null | undefined): boolean =>
  isImageFile(url) || isVideoFile(url);
/**
 * Split attachments into media and non-media files
 */
export const splitAttachmentUrls = (
  urls: string[] | null | undefined = [],
) => ({
  mediaUrls: urls?.filter(isMediaFile),
  fileUrls: urls?.filter((url) => !isMediaFile(url)),
});

/**
 * Shape returned by expo pickers that the creation forms consume. A dropped
 * web `File` is mapped into this shape so both the tap-pick and drag-drop
 * paths flow through the same handlers.
 */
export type PickedAssetLike = {
  uri: string;
  name: string;
  fileName: string;
  mimeType?: string;
  size?: number;
};

/**
 * Returns true if a file's size (in bytes) is within `maxSizeMb` megabytes.
 * A missing/zero size is treated as valid (cannot be measured).
 */
export const isWithinSizeLimit = (
  sizeBytes: number | null | undefined,
  maxSizeMb: number,
): boolean => !sizeBytes || sizeBytes <= maxSizeMb * BYTES_PER_MB;

/**
 * Map a web `File` (from a drag-drop `DataTransfer`) into the asset shape the
 * pickers produce, using an object URL so it works with `<Image>` preview and
 * the upload `fetch(uri)` flow. Web only.
 */
export const webFileToAsset = (file: File): PickedAssetLike => ({
  uri: URL.createObjectURL(file),
  name: file.name,
  fileName: file.name,
  mimeType: file.type || undefined,
  size: file.size,
});
